import { type PointerEvent, useEffect, useRef } from "react";

/** Long enough that crossing the mark on the way somewhere sets nothing off. */
const ARRIVE_MS = 160;

/** Short enough to feel like leaving, long enough to survive a wandering hand. */
const LEAVE_MS = 220;

export type HoverIntent = {
  onPointerMove: (event: PointerEvent) => void;
  onPointerLeave: (event: PointerEvent) => void;
};

/**
 * Hovering brings the column in; leaving takes it away again. Both wait, for
 * opposite reasons: crossing the mark on the way somewhere should set nothing
 * off, and a hand that wanders off the edge of a row should not lose what it
 * was reading.
 *
 * A hover is a hand moving over the mark, not the mark turning up under a
 * hand that is still: closing something that covered it puts the mark under
 * the pointer, and that is not asking for the column.
 *
 * A touch reports a pointer entering and never leaving, so it would bring the
 * column in and strand it there. Down there the mark is a press and nothing
 * else.
 *
 * `stillHovered` is what makes the column answer to the pointer and nothing
 * else. Pressing a way navigates, and a screen travelling swaps the page for
 * a snapshot of itself and back — which reads as a pointer leaving and
 * returning even though the hand never moved. Asking where the pointer
 * actually is, at the moment the column would go, means only a hand that has
 * really left takes it.
 */
export function useHoverIntent(arrive: () => void, leave: () => void, stillHovered: () => boolean) {
  const waiting = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Whether the hand moving over it has already set the column on its way. */
  const arriving = useRef(false);

  function cancel() {
    if (waiting.current) clearTimeout(waiting.current);
    arriving.current = false;
  }

  function after(ms: number, run: () => void) {
    cancel();
    waiting.current = setTimeout(run, ms);
  }

  useEffect(
    () => () => {
      if (waiting.current) clearTimeout(waiting.current);
    },
    [],
  );

  return {
    cancel,
    handlers: {
      onPointerMove: (event: PointerEvent) => {
        if (event.pointerType !== "mouse" || arriving.current) return;
        after(ARRIVE_MS, arrive);
        arriving.current = true;
      },
      onPointerLeave: (event: PointerEvent) => {
        if (event.pointerType !== "mouse") return;
        after(LEAVE_MS, () => {
          if (!stillHovered()) leave();
        });
      },
    } satisfies HoverIntent,
  };
}
